import { useState } from 'react'
import { User, Globe, Users, Building2, Link, X, Loader2, Crown } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { getAvatarColor } from '@/lib/avatar-colors'
import type {
  TemplatePermission,
  PermissionLevel,
  AccessScopeType,
} from '@/lib/api/types'

interface PeopleWithAccessListProps {
  permissions: TemplatePermission[]
  currentUserId?: string
  canManage: boolean
  onUpdatePermission: (permissionId: string, level: PermissionLevel) => Promise<void>
  onRevokePermission: (permissionId: string) => Promise<void>
}

const PERMISSION_OPTIONS: { value: PermissionLevel; label: string }[] = [
  { value: 'View', label: 'Can view' },
  { value: 'Edit', label: 'Can edit' },
  { value: 'Publish', label: 'Can publish' },
  { value: 'Admin', label: 'Full access' },
]

const SCOPE_ICONS: Record<AccessScopeType, typeof User> = {
  User: User,
  Role: Users,
  Team: Building2,
  Everyone: Globe,
  Department: Building2,
  InheritFromHub: Link,
}

export function PeopleWithAccessList({
  permissions,
  currentUserId,
  canManage,
  onUpdatePermission,
  onRevokePermission,
}: PeopleWithAccessListProps) {
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [removingId, setRemovingId] = useState<string | null>(null)

  const handleUpdatePermission = async (permissionId: string, level: PermissionLevel) => {
    setUpdatingId(permissionId)
    try {
      await onUpdatePermission(permissionId, level)
    } finally {
      setUpdatingId(null)
    }
  }

  const handleRevokePermission = async (permissionId: string) => {
    setRemovingId(permissionId)
    try {
      await onRevokePermission(permissionId)
    } finally {
      setRemovingId(null)
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const getScopeIcon = (scopeType: AccessScopeType) => {
    const Icon = SCOPE_ICONS[scopeType] || User
    return Icon
  }

  const getPermissionLabel = (level: PermissionLevel) => {
    const option = PERMISSION_OPTIONS.find((o) => o.value === level)
    return option?.label || level
  }

  const isCurrentUser = (permission: TemplatePermission) => {
    return permission.scopeType === 'User' && permission.scopeId === currentUserId
  }

  // Sort permissions: Owner first, then by scope type, then alphabetically
  const sortedPermissions = [...permissions].sort((a, b) => {
    // Current user's own permission first
    if (isCurrentUser(a) && !isCurrentUser(b)) return -1
    if (!isCurrentUser(a) && isCurrentUser(b)) return 1
    // Then by scope type priority
    const scopePriority: Record<AccessScopeType, number> = {
      User: 1,
      Team: 2,
      Role: 3,
      Department: 4,
      Everyone: 5,
      InheritFromHub: 6,
    }
    const priorityDiff = (scopePriority[a.scopeType] || 99) - (scopePriority[b.scopeType] || 99)
    if (priorityDiff !== 0) return priorityDiff
    // Then alphabetically
    return a.scopeName.localeCompare(b.scopeName)
  })

  if (permissions.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No one has access to this template yet
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {sortedPermissions.map((permission) => {
        const Icon = getScopeIcon(permission.scopeType)
        const isMe = isCurrentUser(permission)
        const isInherited = !!permission.inheritedFromHubId
        const canModify = canManage && !isInherited && !isMe

        return (
          <div
            key={permission.id}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-lg',
              'hover:bg-gray-50 dark:hover:bg-gray-800/50',
              isMe && 'bg-blue-50/50 dark:bg-blue-900/10'
            )}
          >
            {/* Avatar/Icon */}
            <div className="shrink-0">
              {permission.scopeType === 'User' ? (
                <Avatar className="h-9 w-9">
                  <AvatarFallback className={`${getAvatarColor(permission.scopeId || permission.scopeName).bg} ${getAvatarColor(permission.scopeId || permission.scopeName).text}`}>
                    {getInitials(permission.scopeName)}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className="h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Name and details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium truncate">
                  {permission.scopeName}
                </span>
                {isMe && (
                  <Badge variant="secondary" className="text-xs">
                    You
                  </Badge>
                )}
                {isInherited && (
                  <Badge variant="outline" className="text-xs">
                    From {permission.inheritedFromHubName || 'Hub'}
                  </Badge>
                )}
              </div>
              {permission.expiresAt && (
                <div className="text-xs text-muted-foreground">
                  Expires {new Date(permission.expiresAt).toLocaleDateString()}
                </div>
              )}
            </div>

            {/* Permission level */}
            <div className="shrink-0 flex items-center gap-2">
              {canModify ? (
                <>
                  <Select
                    value={permission.permissionLevel}
                    onValueChange={(value) =>
                      handleUpdatePermission(permission.id, value as PermissionLevel)
                    }
                    disabled={updatingId === permission.id}
                  >
                    <SelectTrigger className="w-[130px] h-8">
                      {updatingId === permission.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <SelectValue />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {PERMISSION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRevokePermission(permission.id)}
                    disabled={removingId === permission.id}
                  >
                    {removingId === permission.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </Button>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  {permission.permissionLevel === 'Admin' && isMe && (
                    <Crown className="h-4 w-4 text-amber-500" />
                  )}
                  <span className="text-sm text-muted-foreground">
                    {getPermissionLabel(permission.permissionLevel)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
